/**
 * DashboardTrends Component for v0.7.0
 * Displays trend charts for member growth, ads, and wishes
 * [UPDATED v0.7.2] Added Recharts integration
 */

import { useState, useEffect } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { getDashboardTrends } from '../../../services/dashboardApi';
import type { TrendData } from '../../../types';
import './DashboardTrends.css';

interface DashboardTrendsProps {
  days?: number;
}

const DashboardTrends = ({ days = 30 }: DashboardTrendsProps) => {
  const [trendData, setTrendData] = useState<TrendData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchTrends = async () => {
      setLoading(true);
      setError('');

      try {
        const response = await getDashboardTrends(days);
        if (response.success && response.data) {
          setTrendData(response.data);
        } else {
          setError('取得趨勢數據失敗');
        }
      } catch (err) {
        console.error('Failed to fetch trends:', err);
        setError('載入趨勢數據時發生錯誤');
      } finally {
        setLoading(false);
      }
    };

    fetchTrends();
  }, [days]);

  if (loading) {
    return (
      <div className="dashboard-trends dashboard-trends--loading">
        <div className="dashboard-trends__loading">載入趨勢圖表中...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-trends">
        <div className="dashboard-trends__error">{error}</div>
      </div>
    );
  }

  if (!trendData) {
    return null;
  }

  // Format date for display (MM-DD)
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return `${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
  };

  // Prepare chart data
  const memberChartData = trendData.memberGrowth.map(item => ({
    date: formatDate(item.date),
    count: item.count,
  }));

  const adsChartData = trendData.contentTrend.ads.map(item => ({
    date: formatDate(item.date),
    count: item.count,
  }));

  const wishesChartData = trendData.contentTrend.wishes.map(item => ({
    date: formatDate(item.date),
    count: item.count,
  }));

  return (
    <div className="dashboard-trends">
      <div className="dashboard-trends__header">
        <h2 className="dashboard-trends__title">趨勢分析</h2>
        <div className="dashboard-trends__period">
          過去 {days} 天
        </div>
      </div>

      <div className="dashboard-trends__charts">
        {/* Member Growth Chart */}
        <div className="dashboard-trends__chart">
          <h3 className="dashboard-trends__chart-title">會員增長趨勢</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={memberChartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                style={{ fontSize: '12px' }}
              />
              <YAxis style={{ fontSize: '12px' }} />
              <Tooltip />
              <Legend />
              <Line
                type="monotone"
                dataKey="count"
                stroke="#8884d8"
                strokeWidth={2}
                name="會員數"
                dot={{ fill: '#8884d8', r: 4 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Ads Publication Chart */}
        <div className="dashboard-trends__chart">
          <h3 className="dashboard-trends__chart-title">廣告發布趨勢</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={adsChartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                style={{ fontSize: '12px' }}
              />
              <YAxis style={{ fontSize: '12px' }} />
              <Tooltip />
              <Legend />
              <Line
                type="monotone"
                dataKey="count"
                stroke="#82ca9d"
                strokeWidth={2}
                name="廣告數"
                dot={{ fill: '#82ca9d', r: 4 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Wishes Publication Chart */}
        <div className="dashboard-trends__chart">
          <h3 className="dashboard-trends__chart-title">許願發布趨勢</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={wishesChartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                style={{ fontSize: '12px' }}
              />
              <YAxis style={{ fontSize: '12px' }} />
              <Tooltip />
              <Legend />
              <Line
                type="monotone"
                dataKey="count"
                stroke="#ffc658"
                strokeWidth={2}
                name="許願數"
                dot={{ fill: '#ffc658', r: 4 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default DashboardTrends;
